import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaHeartbeat, 
  FaGraduationCap, 
  FaSeedling, 
  FaBuilding, 
  FaLeaf, 
  FaIndustry,
  FaChevronLeft,
  FaChevronRight,
  FaMobile,
  FaCar,
  FaHome,
  FaChartLine,
  FaUsers,
  FaShieldAlt,
  FaCog,
  FaLightbulb,
  FaGlobe
} from 'react-icons/fa';
import './Segments.css';

const Segments = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const segments = [
    { id: 1, icon: <FaHeartbeat />, title: "Saúde" },
    { id: 2, icon: <FaGraduationCap />, title: "Educação" },
    { id: 3, icon: <FaSeedling />, title: "Agro" },
    { id: 4, icon: <FaBuilding />, title: "Setor Público" },
    { id: 5, icon: <FaLeaf />, title: "Meio Ambiente" },
    { id: 6, icon: <FaIndustry />, title: "Indústria" },
    { id: 7, icon: <FaMobile />, title: "Tecnologia" },
    // { id: 8, icon: <FaShoppingCart />, title: "E-commerce" },
    { id: 9, icon: <FaCar />, title: "Automotivo" },
    // { id: 10, icon: <FaPlane />, title: "Turismo" },
    { id: 11, icon: <FaHome />, title: "Imobiliário" },
    { id: 12, icon: <FaChartLine />, title: "Financeiro" },
    { id: 13, icon: <FaUsers />, title: "RH" },
    { id: 14, icon: <FaShieldAlt />, title: "Segurança" },
    { id: 15, icon: <FaCog />, title: "Logística" },
    { id: 16, icon: <FaLightbulb />, title: "Consultoria" },
    { id: 17, icon: <FaGlobe />, title: "ONGs" },
    { id: 18, icon: <FaIndustry />, title: "Outros" }
  ];

  const itemsPerSlide = isMobile ? 6 : 9;
  const totalSlides = Math.ceil(segments.length / itemsPerSlide);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };

  const cardVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.4
      }
    }
  };

  return (
    <section id="segmentos" className="section segments-section">
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">Atendemos diversos segmentos</h2>
          <p className="section-subtitle">Seja qual for o seu setor, desenvolvemos uma solução para você</p>
        </motion.div>

        <div className="segments-container">
          {!isMobile ? (
            <motion.div
              className="segments-grid"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {segments.map((segment) => (
                <motion.div
                  key={segment.id}
                  className="segment-item"
                  variants={cardVariants}
                  whileHover={{ y: -3, scale: 1.05 }}
                >
                  <div className="segment-icon">
                    {segment.icon}
                  </div>
                  <h3 className="segment-title">{segment.title}</h3>
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <div className="segments-carousel" role="region" aria-label="Carrossel de segmentos atendidos">
              <button 
                className="carousel-btn prev"
                onClick={prevSlide}
                aria-label="Anterior"
              >
                <FaChevronLeft />
              </button>
              
              <div className="carousel-container">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentSlide}
                    className="segments-grid"
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ duration: 0.3 }}
                  >
                    {segments
                      .slice(currentSlide * itemsPerSlide, (currentSlide + 1) * itemsPerSlide)
                      .map((segment) => (
                        <motion.div
                          key={segment.id}
                          className="segment-item"
                          initial={{ y: 20, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          transition={{ duration: 0.4 }}
                          whileHover={{ y: -3, scale: 1.05 }}
                        >
                          <div className="segment-icon">
                            {segment.icon}
                          </div>
                          <h3 className="segment-title">{segment.title}</h3>
                        </motion.div>
                      ))}
                  </motion.div>
                </AnimatePresence>
              </div>
              
              <button 
                className="carousel-btn next"
                onClick={nextSlide}
                aria-label="Próximo"
              >
                <FaChevronRight />
              </button>
            </div>
          )}
          
          {isMobile && (
            <div className="carousel-indicators">
              {Array.from({ length: totalSlides }, (_, index) => (
                <button
                  key={index}
                  className={`indicator ${index === currentSlide ? 'active' : ''}`}
                  onClick={() => setCurrentSlide(index)}
                />
              ))}
            </div>
          )}
        </div>

        <motion.div
          className="segments-cta"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <p>Seu segmento não está listado?</p>
          <button 
            className="btn btn-secondary"
            onClick={() => {
              const element = document.getElementById('contato');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
              }
            }}
          >
            Fale conosco
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default Segments; 